import React, { useState } from 'react';
import type { GameCreatePayload } from '../types';

interface GameCreateModalProps {
  onClose: () => void;
  onSubmit: (payload: GameCreatePayload) => Promise<void>;
}

export function GameCreateModal({ onClose, onSubmit }: GameCreateModalProps) {
  const [name, setName] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(6);
  const [isTeamGame, setIsTeamGame] = useState(false);
  const [teamSize, setTeamSize] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError('Bitte gib einen Spielnamen ein.');
      return;
    }
    if (maxPlayers < 1) {
      setFormError('Mindestens ein Spielerplatz ist nötig.');
      return;
    }
    if (isTeamGame && teamSize < 2) {
      setFormError('Die Teamgröße muss mindestens 2 sein.');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name: trimmed,
        max_players: maxPlayers,
        is_team_game: isTeamGame,
        team_size: isTeamGame ? teamSize : 1,
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Spiel konnte nicht erstellt werden.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-content game-create-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="game-create-title"
      >
        <div className="modal-header">
          <h2 id="game-create-title">🎮 Neues Spiel</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Schließen">✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {formError && <p className="form-error" role="alert">{formError}</p>}
            <div className="form-group">
              <label htmlFor="game-name">Spielname *</label>
              <input
                id="game-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="z.B. Die verlorene Krone"
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="game-max-players">Maximale Spieleranzahl</label>
              <input
                id="game-max-players"
                type="number"
                min={1}
                max={20}
                value={maxPlayers}
                onChange={e => setMaxPlayers(parseInt(e.target.value, 10) || 1)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="game-team-toggle" className="checkbox-label">
                <input
                  id="game-team-toggle"
                  type="checkbox"
                  checked={isTeamGame}
                  onChange={e => setIsTeamGame(e.target.checked)}
                />
                Team-Spiel
              </label>
            </div>
            {isTeamGame && (
              <div className="form-group">
                <label htmlFor="game-team-size">Teamgröße</label>
                <input
                  id="game-team-size"
                  type="number"
                  min={2}
                  max={maxPlayers}
                  value={teamSize}
                  onChange={e => setTeamSize(parseInt(e.target.value, 10) || 2)}
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="medieval-button cancel-btn" onClick={onClose} disabled={submitting}>
              Abbrechen
            </button>
            <button type="submit" className="medieval-button create-btn" disabled={submitting}>
              {submitting ? 'Erstelle…' : 'Spiel erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
